import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
  FiCheckCircle,
  FiCreditCard,
  FiMapPin,
  FiSave,
  FiUpload,
  FiUser
} from "react-icons/fi";
import DoctorMasterLayout from "../doctorlayouts/DoctorMasterLayout";
import { AppContext } from "../../../context/AppContext";
import "../../../assets/css/doctor/doctorpages/ProfileManagement.css";

const ProfileManagement = () => {
  const { baseUrl } = useContext(AppContext);
  const [name, setName]                     = useState("");
  const [email, setEmail]                   = useState("");
  const [phone, setPhone]                   = useState("");
  const [address, setAddress]               = useState("");
  const [specialization, setSpecialization] = useState("");
  const [education, setEducation]           = useState("");
  const [license, setLicense]               = useState("");
  const [experience, setExperience]         = useState("");
  const [bio, setBio]                       = useState("");
  const [location, setLocation]             = useState("");
  const [fee, setFee]                       = useState("");
  const [available, setAvailable]           = useState("Available Today");
  const [profileImage, setProfileImage]     = useState(null);
  const [imagePreview, setImagePreview]     = useState("");
  const [loading, setLoading]               = useState(false);
  const [fetching, setFetching]             = useState(true);
  const [message, setMessage]               = useState({ text: "", type: "" });

  // ── GET profile ───────────────────────────────────────────────
  useEffect(() => {
    axios
      .get(`${baseUrl}/doctor/TherapistProfile/show`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("doctor_token")}`,
        },
      })
      .then((res) => {
        const d = res.data;
        setName(d.name                 || "");
        setEmail(d.email               || "");
        setPhone(d.phone               || "");
        setAddress(d.address           || "");
        setSpecialization(d.specialization || "");
        setEducation(d.education       || "");
        setLicense(d.license           || "");
        setExperience(d.experience     || "");
        setBio(d.bio                   || "");
        setLocation(d.location         || "");
        setFee(d.fee                   || "");
        setAvailable(d.available       || "Available Today");
        setImagePreview(d.profile_image_url || "");
      })
      .catch((err) => console.error("Profile load error:", err))
      .finally(() => setFetching(false));
  }, [baseUrl]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfileImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  // ── POST save ─────────────────────────────────────────────────
  const updateProfile = () => {
    setLoading(true);
    setMessage({ text: "", type: "" });

    const formData = new FormData();
    formData.append("name",           name);
    formData.append("email",          email);
    formData.append("phone",          phone);
    formData.append("address",        address);
    formData.append("specialization", specialization);
    formData.append("education",      education);
    formData.append("license",        license);
    formData.append("experience",     experience);
    formData.append("bio",            bio);
    formData.append("location",       location);
    formData.append("fee",            fee);
    formData.append("available",      available);
    if (profileImage) formData.append("profile_image", profileImage);

    axios
      .post(`${baseUrl}/doctor/TherapistProfile/save`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("doctor_token")}`,
        },
      })
      .then(() => {
        setMessage({ text: "Profile updated successfully!", type: "success" });
      })
      .catch((err) => {
        console.error(err);
        setMessage({ text: "Could not update profile. Please try again.", type: "error" });
      })
      .finally(() => setLoading(false));
  };

  const availabilityClass =
    available === "Available Today"
      ? "pm-badge-today"
      : available === "Available Tomorrow"
      ? "pm-badge-tomorrow"
      : "pm-badge-none";

  const previewImage =
    imagePreview ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(name || "Doctor")}&size=80&background=FF6F00&color=fff&bold=true`;

  if (fetching) {
    return (
      <DoctorMasterLayout>
        <div className="pm-body">
          <p className="pm-loading">Loading profile...</p>
        </div>
      </DoctorMasterLayout>
    );
  }

  return (
    <DoctorMasterLayout>
      <div className="pm-body">
        <div className="pm-container">

          {/* Header */}
          <div className="pm-header">
            <h2 className="pm-title">Profile Management</h2>
            <p className="pm-subtitle">Update your personal and professional details</p>
          </div>

          {message.text && (
            <p className={`pm-message pm-message-${message.type}`}>{message.text}</p>
          )}

          {/* Photo */}
          <div className="pm-section">
            <h3 className="pm-section-title"><FiUser /> Profile Photo</h3>
            <div className="pm-photo-row">
              {imagePreview ? (
                <img src={imagePreview} alt="Preview" className="pm-avatar" />
              ) : (
                <div className="pm-avatar-placeholder"><FiUser /></div>
              )}
              <div className="pm-photo-info">
                <h4>Upload a clear photo</h4>
                <p>JPG or PNG. This is shown on your public profile card.</p>
                <label htmlFor="profileImage" className="pm-upload-btn">
                  <FiUpload /> {imagePreview ? "Change Photo" : "Choose Photo"}
                  <input
                    type="file"
                    id="profileImage"
                    accept="image/*"
                    className="pm-file-input-hidden"
                    onChange={handleImageChange}
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Personal info */}
          <div className="pm-section">
            <h3 className="pm-section-title">Personal Information</h3>
            <div className="pm-form-grid">
              <div className="pm-form-group">
                <label htmlFor="name">Full Name</label>
                <input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Dr. Ahmed Khan" />
              </div>
              <div className="pm-form-group">
                <label htmlFor="phone">Phone</label>
                <input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+92 300 1234567" />
              </div>
              <div className="pm-form-group">
                <label htmlFor="email">Email</label>
                <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="doctor@email.com" />
              </div>
              <div className="pm-form-group">
                <label htmlFor="address">Full Address</label>
                <input id="address" type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Lahore, Punjab, Pakistan" />
              </div>
            </div>
          </div>

          {/* Professional info */}
          <div className="pm-section">
            <h3 className="pm-section-title">Professional Information</h3>
            <div className="pm-form-grid">
              <div className="pm-form-group">
                <label htmlFor="specialization">Specialization</label>
                <input id="specialization" type="text" value={specialization} onChange={(e) => setSpecialization(e.target.value)} placeholder="Clinical Psychologist" />
              </div>
              <div className="pm-form-group">
                <label htmlFor="experience">Years of Experience</label>
                <input id="experience" type="number" value={experience} onChange={(e) => setExperience(e.target.value)} placeholder="15" />
              </div>
              <div className="pm-form-group">
                <label htmlFor="education">Education / Degree</label>
                <input id="education" type="text" value={education} onChange={(e) => setEducation(e.target.value)} placeholder="PhD in Clinical Psychology" />
              </div>
              <div className="pm-form-group">
                <label htmlFor="license">License Number</label>
                <input id="license" type="text" value={license} onChange={(e) => setLicense(e.target.value)} placeholder="PPC-12345" />
              </div>
              <div className="pm-form-group pm-full">
                <label htmlFor="bio">Professional Bio</label>
                <textarea id="bio" rows={5} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Apne baare mein likhen..." />
              </div>
            </div>
          </div>

          {/* Card display settings */}
          <div className="pm-section">
            <h3 className="pm-section-title"><FiCreditCard /> Card Display Settings</h3>
            <div className="pm-form-grid pm-form-grid-3">
              <div className="pm-form-group">
                <label htmlFor="location">City / Location</label>
                <select id="location" value={location} onChange={(e) => setLocation(e.target.value)}>
                  <option value="">-- Select City --</option>
                  <option value="Lahore">Lahore</option>
                  <option value="Karachi">Karachi</option>
                  <option value="Islamabad">Islamabad</option>
                  <option value="Gujranwala">Gujranwala</option>
                  <option value="Rawalpindi">Rawalpindi</option>
                  <option value="Faisalabad">Faisalabad</option>
                  <option value="Multan">Multan</option>
                  <option value="Peshawar">Peshawar</option>
                  <option value="Quetta">Quetta</option>
                </select>
              </div>
              <div className="pm-form-group">
                <label htmlFor="fee">Consultation Fee (Rs.)</label>
                <input id="fee" type="number" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="e.g. 3000" />
              </div>
              <div className="pm-form-group">
                <label htmlFor="available">Availability</label>
                <select id="available" value={available} onChange={(e) => setAvailable(e.target.value)}>
                  <option value="Available Today">Available Today</option>
                  <option value="Available Tomorrow">Available Tomorrow</option>
                  <option value="Not Available">Not Available</option>
                </select>
              </div>
            </div>

            <p className="pm-preview-label">Card preview</p>
            <div className="pm-preview">
              <img src={previewImage} alt="preview" className="pm-preview-img" />
              <div>
                <div className="pm-preview-name">{name || "Dr. Name"}</div>
                <div className="pm-preview-line">
                  {specialization || "Specialization"} &nbsp;•&nbsp; {education || "Degree"}
                </div>
                <div className="pm-preview-meta">
                  <span><FiUser /> {experience ? `${experience} years` : "— years"}</span>
                  <span><FiMapPin /> {location || "City"}</span>
                </div>
                <div className="pm-preview-badges">
                  <span className={`pm-badge ${availabilityClass}`}>{available}</span>
                  <span className="pm-badge pm-badge-verified"><FiCheckCircle /> Verified</span>
                  {fee && <span className="pm-preview-fee">Rs. {Number(fee).toLocaleString()}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Save */}
          <div className="pm-actions">
            <button className="pm-btn-save" onClick={updateProfile} disabled={loading}>
              <FiSave /> {loading ? "Saving..." : "Save Profile"}
            </button>
          </div>

        </div>
      </div>
    </DoctorMasterLayout>
  );
};

export default ProfileManagement;